/**
 * Admin Vault — /vault  (iter 115 rebuild)
 *
 * Restricted to super_user + admin. Rebuilt from scratch to match the master
 * "Thrive Admin Vault" workbook — one tab per sheet, fully inline-editable,
 * global search across every section, no bulk file uploads (data lives here
 * from now on).
 *
 * Tabs:
 *   1. Group Info               (single-record practice-level metadata)
 *   2. Provider Roster          (all 18 credentialing fields, linked to Clinician Roster)
 *   3. Payer Directory          (parent/child grouping + 3 structured "How-To" fields)
 *   4. Credentialing Matrix     (dropdown per cell: Active / Pending / Inactive / Not Enrolled)
 *   5. Contract Rates           (plaintext for admin+super; still encrypted at rest)
 *   6. Payer Enrollment Tracker (Assigned Admin + key dates + status dropdown)
 *   7. Training Matrix          (skill × staff × 0-5 score with red flag on gaps)
 *   8. Nudges                   (secure onboarding invites — unchanged from iter 113)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../lib/api';
import { getOrg } from '../lib/org';
import CredentialingMatrix from '../components/vault/CredentialingMatrix';
import { fmtDate } from '../lib/date';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import PayerCleanupPanel from '../components/PayerCleanupPanel';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  ShieldCheck, Users, Building2, KeyRound, Mail, Eye, Plus, Trash2, RefreshCw, Ban,
  Search, Building, ClipboardList, GraduationCap, Link as LinkIcon, X,
  FileText, Download, Upload as UploadIcon, ExternalLink,
  AlarmClock, CheckCircle, ChevronRight, ChevronDown, ScrollText, Copy, Sparkles,
} from 'lucide-react';
import InfoTooltip from '../components/InfoTooltip';
import { NudgesPanel } from '../components/vault/Nudges';

const TABS = [
  { key: 'group', label: 'Group Info', icon: Building, tip: "Practice-level metadata (TIN, Group NPI, CAQH, phone/fax, mailing address). Only Super Users can edit — Admins see this read-only to prevent accidental changes to legal identifiers." },
  { key: 'documents', label: 'Documents', icon: FileText, tip: "Uploadable shelf for practice PDFs — W-9, letterhead, credentialing cover letters. Super Users upload/delete; every Admin can download. Every download is audit-logged." },
  { key: 'providers', label: 'Provider Roster', icon: Users, tip: "Every clinician's credentialing packet (SSN, DOB, License, NPI, CAQH, taxonomy, address). Sensitive fields (SSN/DOB/License) are Fernet-encrypted at rest — tap Reveal to decrypt (each reveal writes an audit-log row). Rows already in the Clinician Roster show a link badge." },
  { key: 'payers', label: 'Payer Directory', icon: Building2, tip: "Every payer we bill or want to. Parent groups (e.g. United Healthcare) show their child entities (UHC, Optum, UMR, Surest). Each row carries 3 structured how-to blocks so anyone on the team can handle credentialing / denied claims / eligibility without asking." },
  { key: 'credentialing', label: 'Credentialing Matrix', icon: ShieldCheck, tip: "Read-only reference for the billing team. Set the network status per provider × payer (Active / Pending / Inactive / Not Enrolled). Nothing here affects scheduling — it is purely informational." },
  { key: 'rates', label: 'Contract Rates', icon: KeyRound, tip: "Contracted rates per payer + CPT with the auto-computed client charge (full-deductible) and write-off. Effective/Expires dates let you keep historical rates when a payer re-contracts — rows whose Expires date is in the past highlight red. Admin/Super see the numbers directly; they're still Fernet-encrypted at rest." },
  { key: 'enrollment', label: 'Payer Enrollment Tracker', icon: ClipboardList, tip: "Workflow board for payer enrollments. Each payer tracks 4 sub-statuses (Group Contract, Providers Enrolled, ERA Setup, EFT Setup) that roll up to the Overall Status. Use the Table view for detail (expand any row for the full form including notes) or the Kanban view to drag rows between Pending → In Progress → Complete. Review weekly." },
  { key: 'training', label: 'Training Matrix', icon: GraduationCap, tip: "Skill-competency scoreboard for the admin team (0 = not trained, 5 = expert). Any 0 in a skill row is flagged in red — that's your training backlog." },
  { key: 'nudges', label: 'Nudges', icon: Mail, tip: "Send a new provider a secure onboarding link. The form walks them through updating their CAQH profile (adding Thrive as a practice location) and then collects their full credentialing packet (license, NPI, SSN, DOB, address). Everything is Fernet-encrypted at rest. Links expire in 14 days." },
  { key: 'audit', label: 'Audit Log', icon: ScrollText, superOnly: true, tip: "Immutable, hash-chained record of every privileged action and every PHI access — HIPAA §164.312(b). Super Users only. Filter by actor, action, or PHI-only; run integrity verification to detect tampering." },
];

const STATUS_LABEL = {
  active: 'Active', pending: 'Pending', inactive: 'Inactive', not_enrolled: 'Not Enrolled',
  oon: 'OON', rejected: 'Rejected', not_started: 'Not Started',
};
const STATUS_TONE = {
  active: 'bg-green-100 text-green-800 border-green-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  inactive: 'bg-slate-100 text-slate-700 border-slate-200',
  not_enrolled: 'bg-white text-slate-500 border-dashed border-slate-300',
  oon: 'bg-orange-100 text-orange-800 border-orange-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
  not_started: 'bg-slate-50 text-slate-500 border-slate-200',
};

const ENROLLMENT_STATUS = [
  { v: 'pending', l: 'Pending' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'done', l: 'Complete' },
  { v: 'blocked', l: 'Blocked' },
  { v: 'rejected', l: 'Rejected' },
];
const SUB_STATUS = [
  { v: '', l: '—' },
  { v: 'pending', l: 'Pending' },
  { v: 'in_progress', l: 'In Progress' },
  { v: 'complete', l: 'Complete' },
  { v: 'blocked', l: 'Blocked' },
  { v: 'not_applicable', l: 'N/A' },
];
const SUB_STATUS_TONE = {
  complete: 'bg-green-50 text-green-800 border-green-200',
  in_progress: 'bg-amber-50 text-amber-800 border-amber-200',
  pending: 'bg-slate-50 text-slate-700 border-slate-200',
  blocked: 'bg-red-50 text-red-800 border-red-200',
  not_applicable: 'bg-slate-100 text-slate-500 border-slate-200',
  '': 'bg-white text-slate-400 border-dashed border-slate-300',
};
const OVERALL_STATUS_TONE = {
  done: 'bg-green-100 text-green-800 border-green-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  pending: 'bg-slate-100 text-slate-700 border-slate-200',
  blocked: 'bg-red-100 text-red-800 border-red-200',
  rejected: 'bg-red-200 text-red-900 border-red-300',
};
const PRIORITY = ['low', 'medium', 'high', 'critical'];

export default function Vault() {
  // Read the `?tab=<key>` URL param on mount so deep links land on the
  // right tab. Powers the "Open Vault → Create payer" flow from the
  // missing-rate exceptions triage on the Underpayment page.
  const [tab, setTab] = useState(() => {
    if (typeof window === 'undefined') return 'group';
    const sp = new URLSearchParams(window.location.search);
    const t = sp.get('tab');
    return (t && ['group', 'documents', 'providers', 'payers', 'credentialing',
                  'rates', 'enrollments', 'training', 'nudges', 'audit'].includes(t))
      ? t : 'group';
  });
  const [globalQuery, setGlobalQuery] = useState('');
  const [showCleanup, setShowCleanup] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_user' || user?.role === 'admin';
  const isSuperUser = user?.role === 'super_user';

  if (!isAdmin) {
    return (
      <div className="p-8 text-slate-600">You need admin access to view the Admin Vault.</div>
    );
  }

  const isLudo = getOrg() === 'ludo';
  const LUDO_TABS = ['group', 'documents', 'providers', 'payers', 'credentialing', 'nudges'];
  const visibleTabs = TABS.filter(t => (!t.superOnly || isSuperUser) && (!isLudo || LUDO_TABS.includes(t.key)));
  const activeTab = visibleTabs.find(t => t.key === tab) || visibleTabs[0];

  return (
    <div className="space-y-6" data-testid="vault-page">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-[#685B72] via-[#685B72] to-[#839E97] p-8 text-white">
        <div className="text-xs uppercase tracking-widest font-semibold text-white/80 flex items-center">
          Admin Vault
          <InfoTooltip
            testId="tt-vault"
            text="Central credentialing HQ — providers, payers, rates, credentialing status, enrollment workflow, training. Sensitive fields encrypted at rest, plaintext visible to admin/super. Every edit is audit-logged."
            className="text-white/70 hover:text-white"
          />
        </div>
        <h1 className="font-serif text-4xl mt-2 leading-tight">Everything provider · everything payer · one place.</h1>
        <p className="text-white/80 mt-3 max-w-3xl text-sm leading-relaxed">
          Every field is inline-editable. Use the search below to jump to anything (provider, payer, skill, CPT) across all tabs.
        </p>
        <div className="relative mt-5 max-w-lg">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60" />
          <Input
            value={globalQuery}
            onChange={e => setGlobalQuery(e.target.value)}
            placeholder="Search provider, payer, skill, CPT…"
            data-testid="vault-search"
            className="pl-9 bg-white/15 border border-white/25 text-white placeholder:text-white/60 focus-visible:ring-white/40"
          />
          {globalQuery && (
            <button
              onClick={() => setGlobalQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white"
              data-testid="vault-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="vault-tabs">
        {visibleTabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`vault-tab-${key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${tab === key ? 'bg-[#685B72] text-white border-[#685B72]' : 'bg-white text-[#685B72] border-[#E8E5EC] hover:bg-[#F6F2FA]'}`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {activeTab && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <span className="font-semibold uppercase tracking-widest text-slate-400">{activeTab.label}</span>
          <InfoTooltip testId={`tt-tab-${tab}`} text={activeTab.tip} />
        </div>
      )}

      {tab === 'group' && <GroupInfoPanel isSuperUser={user?.role === 'super_user'} />}
      {tab === 'documents' && <DocumentsPanel isSuperUser={user?.role === 'super_user'} />}
      {tab === 'providers' && <ProviderRoster query={globalQuery} />}
      {tab === 'payers' && <PayerDirectory query={globalQuery} onOpenCleanup={() => setShowCleanup(true)} />}
      {tab === 'payers' && isLudo && (
        <div className="space-y-2" data-testid="ludo-payer-rates">
          <h3 className="font-serif text-xl text-[#2D2833]">Contract Rates</h3>
          <p className="text-[12px] text-[#685B72]">ABA codes are billed per 15-minute unit (97151 assessment, 97153 RBT treatment, 97155 BCBA protocol modification, 97156 parent training). Add each payer's rate with its effective date.</p>
          <ContractRates query={globalQuery} />
        </div>
      )}
      {showCleanup && <PayerCleanupPanel onClose={() => setShowCleanup(false)} />}
      {tab === 'credentialing' && <CredentialingMatrix query={globalQuery} matches={matches} />}
      {tab === 'rates' && <ContractRates query={globalQuery} />}
      {tab === 'enrollment' && <EnrollmentTracker query={globalQuery} />}
      {tab === 'training' && <TrainingMatrix query={globalQuery} />}
      {tab === 'nudges' && <NudgesPanel />}
      {tab === 'audit' && isSuperUser && <AuditLogPanel />}
    </div>
  );
}

// ═════════════════ helpers ═════════════════

function matches(q, ...fields) {
  if (!q) return true;
  const s = q.toLowerCase().trim();
  return fields.some(f => (f ?? '').toString().toLowerCase().includes(s));
}

function fmtMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  return `$${n.toFixed(2)}`;
}

// ═════════════════ Group Info ═════════════════

function GroupInfoPanel({ isSuperUser }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const readOnly = !isSuperUser;

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/vault/group-info');
      setForm(data.group_info || {});
    } catch { toast.error('Could not load Group Info'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/vault/group-info', form);
      setForm(data.group_info);
      toast.success('Group Info saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!form) return <Card className="p-6">Loading…</Card>;
  const fields = [
    ['practice_name', 'Practice Name'],
    ['tin', 'TIN / Tax ID'],
    ['group_npi', 'Group NPI'],
    ['main_phone', 'Main Phone'],
    ['fax', 'Fax'],
    ['caqh_group_id', 'CAQH Group ID'],
    ['owner_name', 'Owner / CEO'],
  ];
  return (
    <Card className="p-6 space-y-4" data-testid="vault-group-info">
      {readOnly && (
        <div className="rounded-lg bg-[#F5EFF7] border border-[#D8D6E2] px-4 py-2 text-xs text-[#685B72]" data-testid="group-info-readonly-banner">
          <ShieldCheck size={12} className="inline mr-1" />
          Read-only — only Super Users can edit practice-level metadata. Ping a super user to update.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k} className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</label>
            <Input
              value={form[k] || ''}
              onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
              disabled={readOnly}
              data-testid={`group-field-${k}`}
              className={readOnly ? 'bg-slate-50 text-slate-600' : ''}
            />
          </div>
        ))}
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Mailing Address</label>
          <Input
            value={form.mailing_address || ''}
            onChange={e => setForm(f => ({ ...f, mailing_address: e.target.value }))}
            disabled={readOnly}
            data-testid="group-field-mailing_address"
            className={readOnly ? 'bg-slate-50 text-slate-600' : ''}
          />
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notes</label>
          <Textarea
            value={form.notes || ''}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            disabled={readOnly}
            data-testid="group-field-notes"
            rows={3}
            className={readOnly ? 'bg-slate-50 text-slate-600' : ''}
          />
        </div>
      </div>
      {!readOnly && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} data-testid="group-save">
            {saving ? 'Saving…' : 'Save Group Info'}
          </Button>
        </div>
      )}
    </Card>
  );
}

// ═════════════════ Documents ═════════════════

function DocumentsPanel({ isSuperUser }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadName, setUploadName] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [pickedFile, setPickedFile] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/documents');
      setDocs(data.documents || []);
    } catch { toast.error('Could not load documents'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doUpload = async () => {
    if (!pickedFile) { toast.error('Pick a file first'); return; }
    if (!uploadName.trim()) { toast.error('Give this file a name (e.g. "Thrive W-9")'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pickedFile);
      fd.append('name', uploadName.trim());
      fd.append('description', uploadDesc.trim());
      await api.post('/vault/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded');
      setPickedFile(null); setUploadName(''); setUploadDesc('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const doDownload = async (d) => {
    try {
      const resp = await api.get(`/vault/documents/${d.doc_id}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: d.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = d.file_name || d.name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error('Download failed'); }
  };

  const doDelete = async (d) => {
    if (!window.confirm(`Delete "${d.name}"? This removes the file for everyone.`)) return;
    try {
      await api.delete(`/vault/documents/${d.doc_id}`);
      toast.success('Document deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const isPreviewable = (d) => {
    const m = (d.mime_type || '').toLowerCase();
    return m === 'application/pdf' || m.startsWith('image/');
  };

  const doPreview = async (d) => {
    setPreviewDoc(d);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const resp = await api.get(`/vault/documents/${d.doc_id}/download`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: d.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch {
      toast.error('Could not load preview');
      setPreviewDoc(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewDoc(null);
    setPreviewLoading(false);
  }, [previewUrl]);

  useEffect(() => {
    // Revoke any lingering object URL on unmount
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [previewUrl]);

  const fmtSize = (b) => {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-4" data-testid="vault-documents">
      {isSuperUser && (
        <Card className="p-4 border-dashed border-[#D8D6E2]">
          <div className="text-xs font-semibold uppercase tracking-widest text-[#685B72] mb-3">Upload a new document</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Display Name *</label>
              <Input value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="e.g. Thrive W-9" data-testid="doc-upload-name" />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Description (optional)</label>
              <Input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} placeholder="e.g. Signed 2026 W-9 for payer submissions" data-testid="doc-upload-desc" />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <input
              type="file"
              accept="application/pdf,image/*,.docx,.doc,.xlsx,.xls"
              onChange={e => setPickedFile(e.target.files?.[0] || null)}
              className="text-xs"
              data-testid="doc-upload-file"
            />
            {pickedFile && (
              <span className="text-xs text-[#685B72]">{pickedFile.name} · {fmtSize(pickedFile.size)}</span>
            )}
            <Button
              onClick={doUpload}
              disabled={uploading || !pickedFile || !uploadName.trim()}
              className="ml-auto"
              data-testid="doc-upload-btn"
            >
              <UploadIcon size={13} className="mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
          <div className="text-[10px] text-slate-500 mt-2">PDFs, images, or Office docs up to 25 MB.</div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-slate-600">{docs.length} document{docs.length === 1 ? '' : 's'}</div>
        </div>
        {loading ? (
          <div className="p-6 text-slate-500">Loading…</div>
        ) : docs.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-sm" data-testid="doc-empty">
            No documents yet. {isSuperUser ? 'Upload your first PDF above (W-9, letterhead, etc.).' : 'A super user hasn\'t uploaded any files yet.'}
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 pr-3">Size</th>
                <th className="py-2 pr-3">Uploaded</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {docs.map(d => (
                <tr key={d.doc_id} className="border-b hover:bg-slate-50" data-testid={`doc-row-${d.doc_id}`}>
                  <td className="py-2 pr-3 font-medium text-[#2D2833] flex items-center gap-2">
                    <FileText size={13} className="text-[#685B72]" />
                    {d.name}
                  </td>
                  <td className="py-2 pr-3 text-slate-600 text-xs">{d.description || <span className="text-slate-400 italic">—</span>}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs font-mono">{fmtSize(d.size_bytes)}</td>
                  <td className="py-2 pr-3 text-slate-500 text-xs">{fmtDate(d.uploaded_at)}</td>
                  <td className="py-2 text-right">
                    {isPreviewable(d) && (
                      <Button size="sm" variant="ghost" onClick={() => doPreview(d)} data-testid={`doc-preview-${d.doc_id}`}>
                        <Eye size={13} className="mr-1" /> Preview
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => doDownload(d)} data-testid={`doc-download-${d.doc_id}`}>
                      <Download size={13} className="mr-1" /> Download
                    </Button>
                    {isSuperUser && (
                      <Button size="sm" variant="ghost" onClick={() => doDelete(d)} className="text-[#8A3A3A] hover:text-[#8A3A3A]" data-testid={`doc-delete-${d.doc_id}`}>
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Inline Preview Modal */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => { if (!o) closePreview(); }}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0" data-testid="doc-preview-modal">
          <DialogHeader className="px-6 py-4 border-b border-[#E8E5EC]">
            <DialogTitle className="flex items-center gap-2 text-[#2D2833]">
              <FileText size={16} className="text-[#685B72]" />
              {previewDoc?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {previewDoc?.description || 'Inline preview — close to return to the document list.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-slate-100" data-testid="doc-preview-body">
            {previewLoading && (
              <div className="h-full flex items-center justify-center text-slate-500 text-sm">Loading preview…</div>
            )}
            {!previewLoading && previewUrl && previewDoc && (
              (previewDoc.mime_type || '').startsWith('image/') ? (
                <div className="h-full flex items-center justify-center p-4">
                  <img src={previewUrl} alt={previewDoc.name} className="max-h-full max-w-full object-contain" />
                </div>
              ) : (
                <iframe
                  src={previewUrl}
                  title={previewDoc.name}
                  className="w-full h-full border-0"
                  data-testid="doc-preview-iframe"
                />
              )
            )}
          </div>
          <DialogFooter className="px-6 py-3 border-t border-[#E8E5EC] flex items-center justify-between gap-2 sm:justify-between">
            <div className="text-[10px] text-slate-500">
              {previewDoc && `${fmtSize(previewDoc.size_bytes || 0)} · ${previewDoc.mime_type || 'unknown'}`}
            </div>
            <div className="flex items-center gap-2">
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-[#685B72] hover:underline"
                  data-testid="doc-preview-open-tab"
                >
                  <ExternalLink size={12} /> Open in new tab
                </a>
              )}
              {previewDoc && (
                <Button size="sm" variant="outline" onClick={() => doDownload(previewDoc)} data-testid="doc-preview-download">
                  <Download size={13} className="mr-1" /> Download
                </Button>
              )}
              <Button size="sm" onClick={closePreview} data-testid="doc-preview-close">Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═════════════════ Provider Roster ═════════════════

function ProviderRoster({ query }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/providers');
      setRows(data.providers || []);
    } catch { toast.error('Could not load providers'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openById = useCallback((provider_id) => {
    const p = rows.find(r => r.provider_id === provider_id);
    if (p) setEditing(p);
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter(r => matches(query, r.first_name, r.last_name, r.npi, r.credentials, r.city, r.state)),
    [rows, query],
  );

  return (
    <div className="space-y-4">
      <ProviderExpiryPanel onOpen={openById} />
      <Card className="p-4" data-testid="vault-providers">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">{filtered.length} providers</div>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="provider-add">
          <Plus size={14} className="mr-1" /> Add Provider
        </Button>
      </div>
      {loading ? (
        <div className="p-6 text-slate-500">Loading…</div>
      ) : (
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
              <tr>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Credentials</th>
                <th className="py-2 pr-3">NPI</th>
                <th className="py-2 pr-3">License</th>
                <th className="py-2 pr-3">Expires</th>
                <th className="py-2 pr-3">Clinic</th>
                <th className="py-2 pr-3">Roster</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.provider_id} className="border-b hover:bg-slate-50" data-testid={`provider-row-${p.provider_id}`}>
                  <td className="py-2 pr-3 font-medium">{p.first_name} {p.last_name}</td>
                  <td className="py-2 pr-3 text-slate-600">{p.credentials || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.npi || '—'}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.license_number_masked || '—'}</td>
                  <td className="py-2 pr-3 text-slate-600">{p.license_expires || '—'}</td>
                  <td className="py-2 pr-3 text-slate-600">{p.home_clinic || (p.linked_clinician?.clinics?.[0]) || '—'}</td>
                  <td className="py-2 pr-3">
                    {p.linked_clinician ? (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title={`Linked to ${p.linked_clinician.name}`}>
                        <LinkIcon size={10} /> Linked
                      </span>
                    ) : (
                      <span className="text-[10px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)} data-testid={`provider-edit-${p.provider_id}`}>Edit</Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-slate-400">{query ? `No providers match "${query}"` : "No providers yet"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {(editing || creating) && (
        <ProviderEditor
          provider={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </Card>
    </div>
  );
}

function ProviderExpiryPanel({ onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/vault/expiring', { params: { days: 60 } })
      .then(r => setData(r.data))
      .catch(() => setData({ items: [], count: 0, window_days: 60 }));
  }, []);
  if (!data) return null;
  const items = data.items || [];
  const past = items.filter(i => i.is_past_due);
  const soon = items.filter(i => !i.is_past_due);

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="provider-expiry-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-serif text-2xl text-[#2D2833] flex items-center gap-2">
            <AlarmClock size={18} className="text-[#8A3A3A]" />
            Provider Expirations — next 60 days
            <InfoTooltip testId="tt-expiry" text="Providers whose license expiration falls within the next 60 days. Rows in red are already past due. Tap a name to open that provider's credentialing packet." />
          </h3>
          <div className="text-xs text-[#685B72] mt-0.5">Nothing should lapse silently — renew these before they expire.</div>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="expiry-clear">
          <CheckCircle size={16} /> All clear — no licenses expire in the next 60 days.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="expiry-list">
          {[...past, ...soon].slice(0, 12).map(item => {
            const tone = item.is_past_due
              ? 'bg-red-50 border-red-200 text-red-800'
              : item.days_left <= 14
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-white border-[#E8E5EC] text-[#2D2833]';
            const badgeText = item.is_past_due
              ? `Expired ${Math.abs(item.days_left)}d ago`
              : `In ${item.days_left}d`;
            return (
              <button
                type="button"
                key={item.provider_id}
                onClick={() => onOpen && onOpen(item.provider_id)}
                className={`text-left rounded-lg border p-3 hover:shadow-sm transition-shadow ${tone}`}
                data-testid={`expiry-item-${item.provider_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-semibold text-sm">{item.name} <span className="font-normal opacity-70 text-xs">· {item.credentials}</span></div>
                    <div className="text-[11px] opacity-75 mt-0.5">License expires {item.expires_on}</div>
                  </div>
                  <div className="text-xs font-semibold whitespace-nowrap">{badgeText}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {items.length > 12 && (
        <div className="text-xs text-[#685B72] mt-3">+ {items.length - 12} more — scroll the roster below for the full list.</div>
      )}
    </Card>
  );
}

const PROVIDER_FIELDS = [
  ['first_name', 'First Name', 'text'],
  ['last_name', 'Last Name', 'text'],
  ['gender', 'Gender', 'text'],
  ['npi', 'NPI', 'text'],
  ['caqh_id', 'CAQH ID', 'text'],
  ['credentials', 'Credentials', 'text'],
  ['ssn', 'SSN', 'sensitive'],
  ['dob', 'Date of Birth', 'sensitive'],
  ['license_number', 'License #', 'sensitive'],
  ['license_state', 'License State', 'text'],
  ['license_issued', 'License Issued', 'date'],
  ['license_expires', 'License Expires', 'date'],
  ['taxonomy', 'NPI Taxonomy', 'text'],
  ['home_address', 'Primary Address', 'text'],
  ['suite', 'Suite', 'text'],
  ['city', 'City', 'text'],
  ['state', 'State', 'text'],
  ['zip', 'ZIP', 'text'],
  ['home_clinic', 'Home Clinic', 'text'],
  ['phone', 'Phone', 'text'],
  ['email', 'Email', 'text'],
];

function ProviderEditor({ provider, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [reveals, setReveals] = useState({});
  const [saving, setSaving] = useState(false);
  const isNew = !provider;

  useEffect(() => {
    if (provider) {
      setForm({
        first_name: provider.first_name, last_name: provider.last_name,
        gender: provider.gender, npi: provider.npi, caqh_id: provider.caqh_id,
        credentials: provider.credentials, taxonomy: provider.taxonomy,
        license_state: provider.license_state, license_issued: provider.license_issued,
        license_expires: provider.license_expires, home_address: provider.home_address,
        suite: provider.suite, city: provider.city, state: provider.state, zip: provider.zip,
        home_clinic: provider.home_clinic, phone: provider.phone, email: provider.email,
      });
      // Auto-fill clinic from Clinician Roster if empty
      if (!provider.home_clinic && provider.linked_clinician?.clinics?.[0]) {
        setForm(f => ({ ...f, home_clinic: provider.linked_clinician.clinics[0] }));
      }
    } else {
      setForm({});
    }
    setReveals({});
  }, [provider]);

  const doReveal = async (field) => {
    try {
      const { data } = await api.post(`/vault/providers/${provider.provider_id}/reveal`, { field });
      setReveals(r => ({ ...r, [field]: data.value ?? '' }));
    } catch { toast.error('Could not decrypt this field'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        await api.post('/vault/providers', form);
        toast.success('Provider added');
      } else {
        await api.patch(`/vault/providers/${provider.provider_id}`, form);
        toast.success('Provider saved');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${provider.first_name} ${provider.last_name} from the Vault?`)) return;
    try {
      await api.delete(`/vault/providers/${provider.provider_id}`);
      toast.success('Provider removed');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Provider' : `Edit ${provider.first_name} ${provider.last_name}`}</DialogTitle>
          <DialogDescription>
            All 18 credentialing fields. SSN / DOB / License are encrypted — tap Reveal to see the value (audit-logged).
            {provider?.linked_clinician && (
              <span className="block mt-1 text-emerald-700">
                <LinkIcon size={12} className="inline mr-1" />
                Matched to Clinician Roster ({provider.linked_clinician.clinics?.join(', ') || 'no clinics set'}) — clinic auto-filled, still editable.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PROVIDER_FIELDS.map(([k, label, kind]) => (
            <div key={k} className="space-y-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                <span>{label}</span>
                {kind === 'sensitive' && !isNew && (
                  <button
                    type="button"
                    onClick={() => doReveal(k)}
                    className="text-[10px] text-[#685B72] hover:underline"
                    data-testid={`provider-reveal-${k}`}
                  >
                    <Eye size={11} className="inline mr-0.5" /> Reveal
                  </button>
                )}
              </label>
              <Input
                type={kind === 'date' ? 'date' : 'text'}
                value={
                  kind === 'sensitive' && reveals[k] != null
                    ? reveals[k]
                    : (form[k] || '')
                }
                placeholder={kind === 'sensitive' && !isNew ? (provider[`${k}_masked`] || '') : ''}
                onChange={e => {
                  const val = e.target.value;
                  if (kind === 'sensitive') setReveals(r => ({ ...r, [k]: val }));
                  setForm(f => ({ ...f, [k]: val }));
                }}
                data-testid={`provider-field-${k}`}
              />
            </div>
          ))}
        </div>
        <DialogFooter className="flex justify-between">
          <div>
            {!isNew && (
              <Button variant="destructive" onClick={del} data-testid="provider-delete">
                <Trash2 size={14} className="mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="provider-save">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════ Payer Directory ═════════════════

function PayerDirectory({ query, onOpenCleanup }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/payers');
      setRows(data.payers || []);
    } catch { toast.error('Could not load payers'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link support — `/vault?tab=payers&new=<raw>` opens the Add Payer
  // dialog with the name pre-filled. Powers the one-click "Open Vault →
  // Create payer" flow from the missing-rate exceptions triage on the
  // Underpayment page. We strip the param after consumption so a
  // subsequent tab-switch doesn't reopen the dialog.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const name = sp.get('new');
    if (name) {
      setPrefill({ name });
      setCreating(true);
      sp.delete('new');
      const next = sp.toString();
      const url = window.location.pathname + (next ? `?${next}` : '');
      window.history.replaceState({}, '', url);
    }
  }, []);

  const removePayer = async (p) => {
    if (!window.confirm(
      `Delete "${p.name}"?\n\nThis marks the payer inactive so it stops appearing in dropdowns and payer resolvers. Any existing tracker rows and contract rate history linked to it stay intact.`
    )) return;
    try {
      await api.delete(`/vault/payers/${p.payer_id}`);
      toast.success(`Deleted ${p.name}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Group into parent → children tree so parent rows own their nested
  // children. `_depth` is preserved but child rows now live inside the
  // parent's card and toggle open/closed together. Search still flattens.
  const grouped = useMemo(() => {
    const parents = rows.filter(r => !r.parent_payer_id);
    const childrenByParent = {};
    rows.forEach(r => {
      if (r.parent_payer_id) (childrenByParent[r.parent_payer_id] ||= []).push(r);
    });
    const q = (query || '').trim();
    const rowMatches = (r) => matches(q, r.name, r.code, r.credentialing_contact_email, r.claims_phone);
    const out = [];
    parents.forEach(p => {
      const kids = (childrenByParent[p.payer_id] || []);
      const parentHit = rowMatches(p);
      const matchedKids = kids.filter(rowMatches);
      // Show parent if it or any child matches. Children shown are the
      // ones that match — OR all children when the parent itself matched
      // (so searching a family name reveals its whole tree).
      if (!parentHit && matchedKids.length === 0) return;
      out.push({
        ...p,
        _depth: 0,
        _children: parentHit ? kids : matchedKids,
      });
    });
    // Orphan children (whose parent got filtered but child matched) —
    // show them at the top-level so they aren't lost.
    Object.entries(childrenByParent).forEach(([pid, kids]) => {
      const parentOnList = parents.find(p => p.payer_id === pid);
      if (parentOnList) return; // already handled
      kids.filter(rowMatches).forEach(c => out.push({ ...c, _depth: 0, _children: [] }));
    });
    return out;
  }, [rows, query]);

  const autoGroup = async () => {
    let preview;
    try {
      preview = (await api.post('/vault/payers/auto-group', { apply: false })).data;
    } catch { toast.error('Could not build preview'); return; }
    const nonEmpty = (preview.groups || []).filter(g => (g.children || []).length > 0);
    if (!nonEmpty.length) {
      toast.info('Nothing to auto-group — every payer already has a parent or no family match');
      return;
    }
    const summary = nonEmpty.map(g =>
      `• ${g.family}${g.created_parent ? ' (parent will be created)' : ''}: ${g.children.length} child${g.children.length === 1 ? '' : 'ren'}\n    ${g.children.slice(0,6).join(', ')}${g.children.length > 6 ? '…' : ''}`
    ).join('\n');
    if (!window.confirm(
      `Auto-group ${preview.would_assign_count} payer${preview.would_assign_count === 1 ? '' : 's'}?\n\n${summary}\n\nThis will set the Parent Group on each child. Existing parent assignments are left alone.`
    )) return;
    try {
      const { data } = await api.post('/vault/payers/auto-group', { apply: true });
      toast.success(`Grouped ${data.would_assign_count} payer${data.would_assign_count === 1 ? '' : 's'}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Auto-group failed'); }
  };

  const totalPayers = useMemo(
    () => grouped.reduce((n, p) => n + 1 + (p._children?.length || 0), 0),
    [grouped],
  );

  return (
    <Card className="p-4" data-testid="vault-payers">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#685B72]">
          <span className="font-serif text-[#2D2833]">{totalPayers}</span> payer{totalPayers === 1 ? '' : 's'}
          {grouped.length !== totalPayers && (
            <span className="text-[11px] ml-1">· {grouped.length} parent {grouped.length === 1 ? 'group' : 'groups'}</span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            onClick={() => onOpenCleanup && onOpenCleanup()}
            data-testid="payer-cleanup-open"
            title="Detect and merge duplicate payer entries (BCBS + BCBS TX, UHC + UnitedHealthcare, etc.)"
          >
            <Sparkles size={14} className="mr-1" /> Cleanup Duplicates
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={autoGroup}
            data-testid="payer-auto-group"
            title="Detect United / Aetna / Cigna / BCBS / Humana families and nest aliases under a canonical parent"
          >
            <RefreshCw size={14} className="mr-1" /> Auto-group families
          </Button>
          <Button size="sm" onClick={() => setCreating(true)} data-testid="payer-add">
            <Plus size={14} className="mr-1" /> Add Payer
          </Button>
        </div>
      </div>
      {loading ? <div className="p-6 text-[#685B72]">Loading…</div> : (
        <div className="space-y-1.5" data-testid="payer-list">
          {grouped.map((p, i) => (
            <PayerRow
              key={p.payer_id}
              p={p}
              index={i}
              onEdit={setEditing}
              onDelete={removePayer}
            />
          ))}
          {grouped.length === 0 && (
            <div className="py-8 text-center text-[#685B72]" data-testid="payer-empty">
              {query ? `No payers match "${query}"` : "No payers yet — add your first payer"}
            </div>
          )}
        </div>
      )}
      {(editing || creating) && (
        <PayerEditor
          payer={editing || (prefill && { ...prefill, _isPrefill: true }) || null}
          allPayers={rows}
          onClose={() => { setEditing(null); setCreating(false); setPrefill(null); }}
          onSaved={() => { setEditing(null); setCreating(false); setPrefill(null); load(); }}
        />
      )}
    </Card>
  );
}

// Cycling brand-color accents so admins can visually track each row while
// scrolling a 34+ payer list. Deliberately low-saturation to keep the wall
// of text scannable — the bold payer name always leads the eye.
const PAYER_ACCENTS = [
  { bar: '#685B72', tint: '#F3EFF5' }, // Primary Purple
  { bar: '#4A3B5F', tint: '#EFEAF3' }, // Deep Purple
  { bar: '#839E97', tint: '#EDF3F1' }, // Sage
  { bar: '#B08A3A', tint: '#F9F2E3' }, // Gold
];

function PayerRow({ p, index, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(true);
  const accent = PAYER_ACCENTS[index % PAYER_ACCENTS.length];
  const kids = p._children || [];
  const portal = (p.portal_url || '').split(/\s+/)[0];

  return (
    <div
      className="rounded-lg overflow-hidden bg-white transition-colors"
      style={{
        borderLeft: `3px solid ${accent.bar}`,
        border: `1px solid #E8E5EC`,
        borderLeftWidth: 3,
        borderLeftColor: accent.bar,
      }}
      data-testid={`payer-row-${p.payer_id}`}
    >
      {/* Header summary row — this is all admin sees by default */}
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#FAF8FB]"
        onClick={() => setOpen(o => !o)}
        data-testid={`payer-toggle-${p.payer_id}`}
      >
        <button
          className="text-[#685B72] hover:text-[#2D2833] shrink-0"
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
          title={open ? 'Hide details' : 'Show details'}
          data-testid={`payer-expand-${p.payer_id}`}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <div className="flex-1 min-w-0 flex items-baseline gap-2">
          <span
            className="font-serif text-[15px] text-[#2D2833] truncate"
            data-testid={`payer-name-${p.payer_id}`}
            style={{ fontWeight: 600 }}
          >
            {p.name}
          </span>
          {kids.length > 0 && (
            <span className="text-[10px] text-[#685B72] px-1.5 py-0.5 rounded-full"
                  style={{ background: accent.tint }}>
              {kids.length} nested
            </span>
          )}
        </div>
        <div className="hidden sm:block text-xs text-[#685B72] w-24 truncate">{p.type || '—'}</div>
        <div className="hidden sm:block text-xs font-mono text-[#4A3B5F] w-20 truncate">{p.code || '—'}</div>
        <span
          className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${p.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}
        >
          {p.active ? 'Active' : 'Inactive'}
        </span>
        <div className="shrink-0 flex items-center">
          <Button size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onEdit(p); }}
            className="h-7 text-[12px] text-[#4A3B5F] hover:bg-[#EFEAF3]"
            data-testid={`payer-edit-${p.payer_id}`}
          >Edit</Button>
          <Button
            size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onDelete(p); }}
            className="h-7 text-[#8A3A3A] hover:text-[#8A3A3A] hover:bg-[#F5EAEA] ml-1"
            data-testid={`payer-delete-row-${p.payer_id}`}
            title={`Delete ${p.name}`}
          >
            <Trash2 size={14} />
          </Button>
        </div>
      </div>

      {/* Expandable detail panel — portal, contacts, notes, website */}
      {open && (
        <div className="px-3 pb-3 pt-2 border-t border-[#EFEBF3]" style={{ background: '#FBFAFC' }}
             data-testid={`payer-detail-${p.payer_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
            <DetailKV k="Portal">
              {portal ? (
                <a href={portal} target="_blank" rel="noreferrer" className="text-[#4A3B5F] hover:underline break-all">{portal}</a>
              ) : '—'}
            </DetailKV>
            <DetailKV k="Website">
              {p.website_url ? (
                <a href={p.website_url} target="_blank" rel="noreferrer" className="text-[#4A3B5F] hover:underline break-all">{p.website_url}</a>
              ) : '—'}
            </DetailKV>
            <DetailKV k="Credentialing">
              {p.credentialing_contact_email || p.credentialing_contact_phone
                ? [p.credentialing_contact_email, p.credentialing_contact_phone].filter(Boolean).join(' · ')
                : '—'}
            </DetailKV>
            <DetailKV k="Claims">
              {p.claims_phone || p.claims_fax
                ? [p.claims_phone, p.claims_fax].filter(Boolean).join(' · ')
                : '—'}
            </DetailKV>
            {p.portal_login && (
              <DetailKV k="Portal Login">
                <span className="font-mono text-[11px] text-[#4A3B5F]">{p.portal_login}</span>
              </DetailKV>
            )}
            {p.notes && (
              <div className="col-span-full">
                <div className="text-[10px] uppercase tracking-wider text-[#685B72] mt-1 mb-0.5">Notes</div>
                <div className="text-[12px] text-[#2D2833] whitespace-pre-wrap">{p.notes}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nested child payers — one-click toggle to hide/show */}
      {kids.length > 0 && (
        <div className="border-t border-[#EFEBF3]" style={{ background: accent.tint + '55' }}>
          <button
            onClick={() => setChildrenOpen(o => !o)}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-[#4A3B5F] hover:bg-[#EFEAF3]"
            data-testid={`payer-children-toggle-${p.payer_id}`}
          >
            {childrenOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span className="uppercase tracking-wider">{kids.length} nested payer{kids.length === 1 ? '' : 's'}</span>
          </button>
          {childrenOpen && (
            <div className="pl-6 pr-2 pb-2 space-y-1" data-testid={`payer-children-${p.payer_id}`}>
              {kids.map(c => (
                <NestedPayerRow
                  key={c.payer_id}
                  p={c}
                  accent={accent}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function NestedPayerRow({ p, accent, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const portal = (p.portal_url || '').split(/\s+/)[0];
  return (
    <div
      className="rounded-md bg-white border border-[#EEEAF2]"
      data-testid={`payer-row-${p.payer_id}`}
    >
      <div
        className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[#FAF8FB]"
        onClick={() => setOpen(o => !o)}
      >
        <span className="text-[#B4A7C1] text-[11px]">↳</span>
        <div className="flex-1 min-w-0 flex items-baseline gap-2">
          <span className="font-serif text-[13px] text-[#2D2833] truncate" style={{ fontWeight: 600 }}
                data-testid={`payer-name-${p.payer_id}`}>
            {p.name}
          </span>
        </div>
        <div className="hidden sm:block text-[11px] text-[#685B72] w-20 truncate">{p.type || '—'}</div>
        <div className="hidden sm:block text-[11px] font-mono text-[#4A3B5F] w-16 truncate">{p.code || '—'}</div>
        <span className={`inline-flex text-[9px] px-1.5 py-0.5 rounded-full border shrink-0 ${p.active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
          {p.active ? 'Active' : 'Inactive'}
        </span>
        <div className="shrink-0 flex items-center">
          <Button size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onEdit(p); }}
            className="h-6 text-[11px] text-[#4A3B5F] hover:bg-[#EFEAF3]"
            data-testid={`payer-edit-${p.payer_id}`}
          >Edit</Button>
          <Button
            size="sm" variant="ghost"
            onClick={(e) => { e.stopPropagation(); onDelete(p); }}
            className="h-6 text-[#8A3A3A] hover:text-[#8A3A3A] hover:bg-[#F5EAEA] ml-0.5"
            data-testid={`payer-delete-row-${p.payer_id}`}
            title={`Delete ${p.name}`}
          >
            <Trash2 size={12} />
          </Button>
        </div>
      </div>
      {open && (
        <div className="px-3 pb-2 pt-1.5 border-t border-[#F0EDF4] text-[11px]"
             data-testid={`payer-detail-${p.payer_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
            <DetailKV k="Portal" small>{portal ? <a href={portal} target="_blank" rel="noreferrer" className="text-[#4A3B5F] hover:underline break-all">{portal}</a> : '—'}</DetailKV>
            <DetailKV k="Credentialing" small>{p.credentialing_contact_email || p.credentialing_contact_phone || '—'}</DetailKV>
            {p.notes && <div className="col-span-full text-[11px] text-[#2D2833] whitespace-pre-wrap mt-1">{p.notes}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function DetailKV({ k, children, small }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className={`${small ? 'text-[9px]' : 'text-[10px]'} uppercase tracking-wider text-[#685B72] shrink-0 w-24`}>{k}</div>
      <div className="text-[#2D2833] flex-1 min-w-0 break-words">{children}</div>
    </div>
  );
}

function PayerEditor({ payer, allPayers, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Distinguish "editing an existing payer" from "adding a new one with a
  // pre-filled name from a deep-link". Both branches use the same form.
  const isNew = !payer || payer._isPrefill;

  useEffect(() => {
    if (payer && !payer._isPrefill) {
      setForm({ ...payer });
    } else if (payer && payer._isPrefill) {
      setForm({ name: payer.name || '', type: 'commercial', active: true });
    } else {
      setForm({ type: 'commercial', active: true });
    }
  }, [payer]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        // iter 143 — inline rate fields let admin capture contract rate
        // during payer creation. Fields are optional; only sent when the
        // CPT + rate pair is present so the backend skips rate creation
        // silently when the admin hasn't filled them in.
        const payload = { ...form };
        if (form.initial_cpt_code && form.initial_contract_rate) {
          payload.initial_cpt_code = String(form.initial_cpt_code).trim().toUpperCase();
          payload.initial_contract_rate = form.initial_contract_rate;
          payload.initial_effective_date = form.initial_effective_date || '';
        } else {
          delete payload.initial_cpt_code;
          delete payload.initial_contract_rate;
          delete payload.initial_effective_date;
        }
        await api.post('/vault/payers', payload);
        toast.success(payload.initial_cpt_code ? 'Payer + contract rate added' : 'Payer added');
      } else {
        await api.patch(`/vault/payers/${payer.payer_id}`, form);
        toast.success('Payer saved');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${payer.name}?`)) return;
    try {
      await api.delete(`/vault/payers/${payer.payer_id}`);
      toast.success('Payer removed');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const parentOptions = allPayers.filter(p => !p.parent_payer_id && p.payer_id !== payer?.payer_id);

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? 'Add Payer' : `Edit ${payer.name}`}</DialogTitle>
          <DialogDescription>Inline-edit any field. Set a Parent Group to nest this payer under a larger family (e.g. UMR under United Healthcare).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Name" v={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} tid="payer-field-name" />
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Type</label>
            <select
              value={form.type || 'commercial'}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="payer-field-type"
            >
              <option value="commercial">Commercial</option>
              <option value="medicaid">Medicaid</option>
              <option value="medicare">Medicare</option>
              <option value="government">Government</option>
              <option value="other">Other</option>
            </select>
          </div>
          <Field label="Payer Code / ID" v={form.code} onChange={v => setForm(f => ({ ...f, code: v }))} tid="payer-field-code" />
          <div className="space-y-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Parent Group</label>
            <select
              value={form.parent_payer_id || ''}
              onChange={e => setForm(f => ({ ...f, parent_payer_id: e.target.value || null }))}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
              data-testid="payer-field-parent"
            >
              <option value="">— No parent —</option>
              {parentOptions.map(p => (
                <option key={p.payer_id} value={p.payer_id}>{p.name}</option>
              ))}
            </select>
          </div>
          <Field label="Portal URL(s)" v={form.portal_url} onChange={v => setForm(f => ({ ...f, portal_url: v }))} tid="payer-field-portal" />
          <Field label="Claims / Auth Phone" v={form.claims_phone} onChange={v => setForm(f => ({ ...f, claims_phone: v }))} tid="payer-field-phone" />
          <Field label="Credentialing Contact — Name" v={form.credentialing_contact_name} onChange={v => setForm(f => ({ ...f, credentialing_contact_name: v }))} tid="payer-field-cred-name" />
          <Field label="Credentialing Contact — Email" v={form.credentialing_contact_email} onChange={v => setForm(f => ({ ...f, credentialing_contact_email: v }))} tid="payer-field-cred-email" />
          <Field label="Credentialing Contact — Phone" v={form.credentialing_contact_phone} onChange={v => setForm(f => ({ ...f, credentialing_contact_phone: v }))} tid="payer-field-cred-phone" />
          <Field label="Auth Required?" v={form.auth_required} onChange={v => setForm(f => ({ ...f, auth_required: v }))} tid="payer-field-auth" />
        </div>
        <div className="grid grid-cols-1 gap-3 mt-3">
          <Textarea3
            label="How to manage credentialing"
            v={form.how_to_credentialing}
            onChange={v => setForm(f => ({ ...f, how_to_credentialing: v }))}
            tid="payer-field-how-creds"
            tip="Where to add/term providers, roster update format, portal link, credentialing team contact."
          />
          <Textarea3
            label="How to manage denied claims"
            v={form.how_to_denied_claims}
            onChange={v => setForm(f => ({ ...f, how_to_denied_claims: v }))}
            tid="payer-field-how-denied"
            tip="Steps to investigate + appeal a denial for this payer (portal, phone, fax, forms)."
          />
          <Textarea3
            label="How to check benefits"
            v={form.how_to_check_benefits}
            onChange={v => setForm(f => ({ ...f, how_to_check_benefits: v }))}
            tid="payer-field-how-benefits"
            tip="Eligibility portal(s), phone line, and any prep needed before verifying benefits."
          />
          <Textarea3
            label="Special rules & notes"
            v={form.special_rules}
            onChange={v => setForm(f => ({ ...f, special_rules: v }))}
            tid="payer-field-rules"
            tip="F84 rules, filing-provider quirks, member-ID overrides, contract caveats — any gotcha to remember."
          />
        </div>
        {/* ── iter 143 — Inline contract-rate entry (create flow only) ── */}
        {isNew && (
          <div className="mt-4 p-4 rounded-lg border border-[#B08A3A44] bg-[#FBF8F4]" data-testid="payer-initial-rate-section">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#685B72] mb-1">
              Contract Rate (optional)
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              Add the first contract rate for this payer now. If you skip this, the payer will still appear on the Contract Rates tab so you can add one later.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Billing Code (CPT)</label>
                <Input
                  value={form.initial_cpt_code || ''}
                  onChange={e => setForm(f => ({ ...f, initial_cpt_code: e.target.value }))}
                  placeholder="e.g. 92526"
                  data-testid="payer-initial-cpt"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Contracted Rate ($)</label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.initial_contract_rate || ''}
                  onChange={e => setForm(f => ({ ...f, initial_contract_rate: e.target.value }))}
                  placeholder="0.00"
                  data-testid="payer-initial-rate"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Effective From</label>
                <Input
                  type="date"
                  value={form.initial_effective_date || ''}
                  onChange={e => setForm(f => ({ ...f, initial_effective_date: e.target.value }))}
                  data-testid="payer-initial-eff-date"
                />
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <input type="checkbox" checked={!!form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} data-testid="payer-field-active" />
          <label className="text-sm">Active payer</label>
        </div>
        <DialogFooter className="flex justify-between">
          <div>
            {!isNew && <Button variant="destructive" onClick={del} data-testid="payer-delete"><Trash2 size={14} className="mr-1" /> Delete</Button>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="payer-save">{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, v, onChange, tid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      <Input value={v || ''} onChange={e => onChange(e.target.value)} data-testid={tid} />
    </div>
  );
}
function Textarea3({ label, v, onChange, tid, tip }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 flex items-center">
        {label}
        {tip && <InfoTooltip text={tip} testId={`${tid}-tip`} />}
      </label>
      <Textarea rows={3} value={v || ''} onChange={e => onChange(e.target.value)} data-testid={tid} />
    </div>
  );
}

// ═════════════════ Credentialing Matrix ═════════════════

// ═════════════════ Contract Rates ═════════════════
// Rate history model: each (payer, CPT) is a *timeline* of rate rows, one
// per period, each carrying Effective From + Effective To. Admin only
// enters the new rate's Effective From — the backend auto-closes the
// previous open row at (from - 1 day). Historical entries stay editable
// so an admin can correct a bad entry after the fact.

function fmtVaultDate(v) {
  // MM-DD-YYYY per Underpayment module convention. Empty → em-dash.
  if (!v) return '—';
  const iso = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return iso ? `${iso[2]}-${iso[3]}-${iso[1]}` : String(v);
}

function ContractRates({ query }) {
  const [rows, setRows] = useState([]);
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingFor, setAddingFor] = useState(null); // {payer_id, payer_name, cpt_code}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // iter 143 — pull payers alongside rates so a brand-new payer with
      // zero rates still appears here as an empty card with an "Add rate"
      // CTA (Fix 1). Contract Rates is the intake point for underpayment
      // tracking, so a payer that never surfaces here becomes invisible
      // to the whole downstream detector.
      const [ratesResp, payersResp] = await Promise.all([
        api.get('/vault/contract-rates'),
        api.get('/vault/payers'),
      ]);
      setRows(ratesResp.data.rates || []);
      setPayers((payersResp.data.payers || []).filter(p => p.active !== false));
    } catch { toast.error('Could not load contract rates'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => rows.filter(r => matches(query, r.payer_name, r.cpt_code, r.notes)),
    [rows, query],
  );

  // ── Two-level grouping: payer → CPT → timeline ──
  // The user wants ONE payer card, with each CPT rendered as its own
  // collapsible timeline inside so a long roster no longer produces N×M
  // scattered cards. Formulas don't change — the same (payer_id, CPT,
  // effective_date) tuple still resolves the rate at refresh time.
  const payerGroups = useMemo(() => {
    const byPayer = new Map();
    // Seed the map with every active payer so zero-rate payers still
    // render — the ordering + Add-rate CTA are what let admin fix the
    // underpayment tracker without leaving this screen.
    const q = (query || '').trim().toLowerCase();
    for (const p of payers) {
      if (q && !(p.name || '').toLowerCase().includes(q)) continue;
      byPayer.set(p.payer_id, {
        payer_id: p.payer_id,
        payer_name: p.name,
        codes: new Map(),
      });
    }
    for (const r of filtered) {
      const pKey = r.payer_id;
      if (!byPayer.has(pKey)) {
        byPayer.set(pKey, {
          payer_id: r.payer_id,
          payer_name: r.payer_name,
          codes: new Map(),  // cpt_code → {cpt_code, rates: []}
        });
      }
      const codesMap = byPayer.get(pKey).codes;
      if (!codesMap.has(r.cpt_code)) {
        codesMap.set(r.cpt_code, { cpt_code: r.cpt_code, rates: [] });
      }
      codesMap.get(r.cpt_code).rates.push(r);
    }
    // Sort rates inside each CPT — oldest first, empty effective_date at
    // the end (legacy undated rows). Tag the last-current row for badge.
    const list = [];
    for (const pg of byPayer.values()) {
      const codes = Array.from(pg.codes.values());
      for (const cd of codes) {
        cd.rates.sort((a, b) => {
          const ax = a.effective_date || '9999-12-31';
          const bx = b.effective_date || '9999-12-31';
          return ax.localeCompare(bx);
        });
        cd.rates.forEach((r, i) => {
          r._is_current = !r.expires_date && i === cd.rates.length - 1;
        });
      }
      codes.sort((a, b) => (a.cpt_code || '').localeCompare(b.cpt_code || ''));
      list.push({ ...pg, codes });
    }
    // Payers with rates first (higher signal), then empty payers.
    list.sort((a, b) => {
      const aEmpty = a.codes.length === 0 ? 1 : 0;
      const bEmpty = b.codes.length === 0 ? 1 : 0;
      if (aEmpty !== bEmpty) return aEmpty - bEmpty;
      return (a.payer_name || '').localeCompare(b.payer_name || '');
    });
    return list;
  }, [filtered, payers, query]);

  const setField = async (rate_id, field, value) => {
    try {
      const { data: updated } = await api.patch(`/vault/contract-rates/${rate_id}`, { [field]: value });
      setRows(rs => rs.map(r => r.rate_id === rate_id ? updated : r));
    } catch { toast.error('Save failed'); }
  };

  const removeRate = async (rate_id) => {
    if (!window.confirm('Remove this rate row? Historical rows should usually be corrected, not deleted.')) return;
    try {
      await api.delete(`/vault/contract-rates/${rate_id}`);
      setRows(rs => rs.filter(r => r.rate_id !== rate_id));
      toast.success('Rate row deleted');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;

  return (
    <div className="space-y-3" data-testid="vault-rates">
      <div className="text-sm text-slate-600">
        {payerGroups.length} payer{payerGroups.length === 1 ? '' : 's'} · each shows every CPT with its full rate history.
        Adding a new rate auto-closes the previous one on (Effective From − 1 day).
      </div>
      {payerGroups.map((pg, idx) => (
        <PayerRatesCard
          key={pg.payer_id}
          payerGroup={pg}
          index={idx}
          onSetField={setField}
          onDelete={removeRate}
          onAddCode={(cpt_code) => setAddingFor({
            payer_id: pg.payer_id,
            payer_name: pg.payer_name,
            cpt_code,
          })}
          onAddNewCode={() => setAddingFor({
            payer_id: pg.payer_id,
            payer_name: pg.payer_name,
            cpt_code: '',
          })}
        />
      ))}
      {payerGroups.length === 0 && (
        <Card className="p-6 text-center text-sm text-slate-500">
          {query ? `No rates match "${query}"` : "No contract rates yet"}
        </Card>
      )}
      {addingFor && (
        <AddRateDialog
          target={addingFor}
          onClose={() => setAddingFor(null)}
          onCreated={() => { setAddingFor(null); load(); }}
        />
      )}
    </div>
  );
}

// One card per payer. Inside, every CPT the payer has a rate for is
// rendered as its own collapsible sub-timeline. Add-rate buttons live
// at the CPT level (append a new period) and at the payer level (bring
// in a brand-new CPT for this payer). The `index` prop drives the
// cycling brand-color header banner so admins can visually anchor a
// specific payer when scrolling through a long list of rate cards.
function PayerRatesCard({ payerGroup, index = 0, onSetField, onDelete, onAddCode, onAddNewCode }) {
  const { payer_id, payer_name, codes } = payerGroup;
  const totalPeriods = codes.reduce((n, c) => n + c.rates.length, 0);
  const [collapsed, setCollapsed] = useState(new Set());
  const toggle = (code) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };
  const accent = PAYER_ACCENTS[index % PAYER_ACCENTS.length];

  return (
    <Card
      className="overflow-hidden border-[#E8E5EC]"
      data-testid={`rate-payer-${payer_id}`}
      style={{ borderLeft: `4px solid ${accent.bar}` }}
    >
      {/* Colored banner: payer name is the loudest element on the card */}
      <div
        className="flex items-baseline gap-3 px-4 py-3"
        style={{ background: accent.tint }}
        data-testid={`rate-payer-banner-${payer_id}`}
      >
        <div
          className="font-serif text-xl text-[#2D2833]"
          style={{ fontWeight: 700, letterSpacing: '-0.01em' }}
          data-testid={`rate-payer-name-${payer_id}`}
        >
          {payer_name}
        </div>
        <div className="text-[11px] text-[#685B72]">
          {codes.length} code{codes.length === 1 ? '' : 's'} · {totalPeriods} rate period{totalPeriods === 1 ? '' : 's'}
        </div>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={onAddNewCode}
          className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
          data-testid={`rate-payer-add-code-${payer_id}`}
        >
          + Add rate for new code
        </Button>
      </div>

      <div className="space-y-3 p-4">
        {codes.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#B08A3A66] bg-[#FBF8F4] p-4 text-center"
               data-testid={`rate-payer-empty-${payer_id}`}>
            <div className="text-[13px] text-[#685B72] mb-2">
              No contract rates on file for this payer yet.
            </div>
            <div className="text-[11px] text-slate-500 mb-3">
              Adding a rate here immediately makes it available to the Underpayment tracker on the next refresh.
            </div>
            <Button
              size="sm"
              onClick={onAddNewCode}
              className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
              data-testid={`rate-payer-first-rate-${payer_id}`}
            >
              + Add first rate
            </Button>
          </div>
        )}
        {codes.map(cd => {
          const isCollapsed = collapsed.has(cd.cpt_code);
          const currentRate = cd.rates.find(r => r._is_current);
          return (
            <div key={cd.cpt_code} className="rounded-lg border border-[#E8E5EC] bg-white overflow-hidden" data-testid={`rate-code-${payer_id}-${cd.cpt_code}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-[#FAF8FB]">
                <button
                  onClick={() => toggle(cd.cpt_code)}
                  className="text-slate-500 hover:text-[#2D2833]"
                  data-testid={`rate-code-toggle-${payer_id}-${cd.cpt_code}`}
                  title={isCollapsed ? 'Expand history' : 'Collapse history'}
                >
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                </button>
                <div className="font-mono text-sm text-[#2D2833]">{cd.cpt_code}</div>
                <div className="text-[11px] text-[#685B72]">{cd.rates.length} period{cd.rates.length === 1 ? '' : 's'}</div>
                {currentRate && (
                  <div className="text-[11px] px-2 py-0.5 rounded-full bg-[#E4EEE9] text-[#3B6F5D] font-semibold">
                    current · {fmtMoney(currentRate.contract_rate)}
                  </div>
                )}
                <div className="flex-1" />
                <Button
                  size="sm" variant="outline"
                  onClick={() => onAddCode(cd.cpt_code)}
                  className="h-7 text-[11px]"
                  data-testid={`rate-code-add-${payer_id}-${cd.cpt_code}`}
                >
                  + New rate
                </Button>
              </div>
              {!isCollapsed && (
                <div className="overflow-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b">
                      <tr>
                        <th className="py-2 px-3">Effective From</th>
                        <th className="py-2 px-3">Effective To</th>
                        <th className="py-2 px-3 text-right">Rate</th>
                        <th className="py-2 px-3 text-right">Client (Full Ded.)</th>
                        <th className="py-2 px-3 text-right">Write-off</th>
                        <th className="py-2 px-3">Notes</th>
                        <th className="py-2 px-3 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cd.rates.map(r => {
                        const expired = r.expires_date && r.expires_date < new Date().toISOString().slice(0,10);
                        return (
                          <tr
                            key={r.rate_id}
                            className={`border-b hover:bg-slate-50 ${r._is_current ? 'bg-[#EFE9F5]/40' : ''} ${expired ? 'text-slate-500' : ''}`}
                            data-testid={`rate-row-${r.rate_id}`}
                          >
                            <td className="py-2 px-3">
                              <EditDate value={r.effective_date} onCommit={v => onSetField(r.rate_id, 'effective_date', v)} tid={`rate-eff-${r.rate_id}`} display={fmtVaultDate} />
                            </td>
                            <td className="py-2 px-3">
                              {r._is_current ? (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#E4EEE9] text-[#3B6F5D]" title="Currently active rate">
                                  current
                                </span>
                              ) : (
                                <EditDate value={r.expires_date} onCommit={v => onSetField(r.rate_id, 'expires_date', v)} tid={`rate-exp-${r.rate_id}`} display={fmtVaultDate} />
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              <EditNumber value={r.contract_rate} onCommit={v => onSetField(r.rate_id, 'contract_rate', v)} tid={`rate-contract-${r.rate_id}`} />
                            </td>
                            <td className="py-2 px-3 text-right">
                              <EditNumber value={r.client_charge_full_ded} onCommit={v => onSetField(r.rate_id, 'client_charge_full_ded', v)} tid={`rate-client-${r.rate_id}`} />
                            </td>
                            <td className="py-2 px-3 text-right text-slate-600 font-mono">{fmtMoney(r.write_off)}</td>
                            <td className="py-2 px-3 text-slate-600 text-xs">
                              <EditText value={r.notes} onCommit={v => onSetField(r.rate_id, 'notes', v)} tid={`rate-notes-${r.rate_id}`} placeholder="—" />
                            </td>
                            <td className="py-2 px-3">
                              <button
                                onClick={() => onDelete(r.rate_id)}
                                className="p-1 rounded text-slate-400 hover:text-[#8A3A3A] hover:bg-[#F5EAEA]"
                                title="Delete this rate row"
                                data-testid={`rate-del-${r.rate_id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function AddRateDialog({ target, onClose, onCreated }) {
  const [rate, setRate] = useState('');
  const [clientCharge, setClientCharge] = useState('');
  const [effFrom, setEffFrom] = useState('');
  const [notes, setNotes] = useState('');
  const [cpt, setCpt] = useState(target.cpt_code || '');
  const [saving, setSaving] = useState(false);
  const cptLocked = !!target.cpt_code;  // pre-selected CPT — can't be changed

  const save = async () => {
    const cptClean = (cpt || '').trim();
    if (!cptClean) { toast.error('CPT code is required'); return; }
    if (!rate) { toast.error('Rate is required'); return; }
    if (!effFrom) { toast.error('Effective From is required — the auto-close needs it'); return; }

    // ── Overlap warning ──
    // Fetch the current history for this (payer, CPT) and check whether
    // the chosen Effective From lands inside a period that already has
    // BOTH bounds set (i.e. not the auto-closable current-open row).
    // If so, warn — an overlap probably means the admin misread the
    // timeline and would otherwise clone a rate atop a closed history.
    try {
      const { data: hist } = await api.get('/vault/contract-rates/history', {
        params: { payer_id: target.payer_id, cpt_code: cptClean },
      });
      const clash = (hist.rates || []).find(r => {
        const eff = r.effective_date || '';
        const exp = r.expires_date   || '';
        if (!eff || !exp) return false;  // ignore open-ended row (auto-close handles it)
        return effFrom >= eff && effFrom <= exp;
      });
      if (clash) {
        const proceed = window.confirm(
          `This overlaps an existing closed rate period:\n\n` +
          `  ${fmtVaultDate(clash.effective_date)} → ${fmtVaultDate(clash.expires_date)} @ ${fmtMoney(clash.contract_rate)}\n\n` +
          `Adding a rate inside a closed period usually means you meant a different Effective From date. Continue anyway?`
        );
        if (!proceed) return;
      }
    } catch { /* non-blocking — the POST below is the real gate */ }

    setSaving(true);
    try {
      await api.post('/vault/contract-rates', {
        payer_id: target.payer_id,
        cpt_code: cptClean,
        contract_rate: rate,
        client_charge_full_ded: clientCharge || null,
        effective_date: effFrom,
        notes,
      });
      toast.success(cptLocked
        ? 'Rate added — previous rate auto-closed'
        : `Rate added for ${cptClean}`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rate-add-dialog">
        <DialogHeader>
          <DialogTitle>
            {cptLocked
              ? `New rate for ${target.payer_name} · ${target.cpt_code}`
              : `New rate for ${target.payer_name}`}
          </DialogTitle>
          <DialogDescription>
            {cptLocked
              ? `The previous "current" rate for this combo will auto-close on the day before this rate's Effective From date.`
              : `Enter a new CPT code for ${target.payer_name}. Adding a rate for a code the payer doesn't have yet just creates the first period.`}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">CPT code</label>
            <Input
              value={cpt}
              disabled={cptLocked}
              onChange={e => setCpt(e.target.value.toUpperCase())}
              placeholder="e.g. 92507"
              data-testid="rate-add-cpt"
            />
          </div>
          <div className="col-span-1 space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Rate ($)</label>
            <Input
              type="number" step="0.01" value={rate}
              onChange={e => setRate(e.target.value)}
              placeholder="e.g. 95.00" data-testid="rate-add-amount"
            />
          </div>
          <div className="col-span-1 space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Client charge (full ded)</label>
            <Input
              type="number" step="0.01" value={clientCharge}
              onChange={e => setClientCharge(e.target.value)}
              placeholder="optional" data-testid="rate-add-client"
            />
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Effective From</label>
            <Input
              type="date" value={effFrom}
              onChange={e => setEffFrom(e.target.value)}
              data-testid="rate-add-effective"
            />
            <div className="text-[10px] text-[#685B72]">Displayed as {fmtVaultDate(effFrom) || 'MM-DD-YYYY'}</div>
          </div>
          <div className="col-span-2 space-y-1">
            <label className="text-[10px] uppercase tracking-widest text-[#685B72] font-semibold">Notes</label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="optional" data-testid="rate-add-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="rate-add-cancel">Cancel</Button>
          <Button
            onClick={save} disabled={saving}
            className="bg-[#685B72] hover:bg-[#4A3B5F] text-white"
            data-testid="rate-add-save"
          >
            {saving ? 'Saving…' : 'Add rate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditNumber({ value, onCommit, tid }) {
  const [v, setV] = useState(value ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value ?? ''); }, [value]);
  if (!editing) return (
    <button onClick={() => setEditing(true)} className="font-mono hover:underline" data-testid={tid}>
      {fmtMoney(value)}
    </button>
  );
  return (
    <input
      autoFocus
      type="number"
      step="0.01"
      value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (value ?? '')) onCommit(v === '' ? null : Number(v)); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value ?? ''); setEditing(false); } }}
      className="w-24 border border-slate-300 rounded px-2 py-1 text-right"
      data-testid={`${tid}-input`}
    />
  );
}
function EditText({ value, onCommit, tid, placeholder }) {
  const [v, setV] = useState(value ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value ?? ''); }, [value]);
  if (!editing) return (
    <button onClick={() => setEditing(true)} className="text-left hover:underline w-full" data-testid={tid}>
      {value || <span className="text-slate-400">{placeholder || 'Edit…'}</span>}
    </button>
  );
  return (
    <input
      autoFocus
      value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (value ?? '')) onCommit(v); }}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setV(value ?? ''); setEditing(false); } }}
      className="w-full border border-slate-300 rounded px-2 py-1"
      data-testid={`${tid}-input`}
    />
  );
}

// ═════════════════ Payer Enrollment Tracker ═════════════════

function EnrollmentTracker({ query }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('table');  // 'table' | 'kanban'

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/payer-enrollment');
      setRows(data.rows || []);
    } catch { toast.error('Could not load enrollment tracker'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => rows.filter(r => matches(query, r.payer_name, r.assigned_admin, r.next_action, r.contact, r.notes)),
    [rows, query],
  );

  const setField = async (enrollment_id, field, value) => {
    try {
      const { data: updated } = await api.patch(`/vault/payer-enrollment/${enrollment_id}`, { [field]: value });
      setRows(rs => rs.map(r => r.enrollment_id === enrollment_id ? updated : r));
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;

  return (
    <Card className="p-4 overflow-auto" data-testid="vault-enrollment">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-slate-600">{filtered.length} enrollment{filtered.length === 1 ? '' : 's'}</div>
        <div className="inline-flex rounded-lg border border-[#E8E5EC] overflow-hidden" data-testid="enroll-view-toggle">
          <button
            onClick={() => setView('table')}
            className={`px-3 py-1 text-xs font-semibold ${view === 'table' ? 'bg-[#685B72] text-white' : 'bg-white text-[#685B72] hover:bg-[#F6F2FA]'}`}
            data-testid="enroll-view-table"
          >Table</button>
          <button
            onClick={() => setView('kanban')}
            className={`px-3 py-1 text-xs font-semibold ${view === 'kanban' ? 'bg-[#685B72] text-white' : 'bg-white text-[#685B72] hover:bg-[#F6F2FA]'}`}
            data-testid="enroll-view-kanban"
          >Kanban</button>
        </div>
      </div>

      {view === 'table' ? (
        <EnrollmentTable filtered={filtered} setField={setField} />
      ) : (
        <EnrollmentKanban filtered={filtered} setField={setField} />
      )}
    </Card>
  );
}

function EnrollmentTable({ filtered, setField }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-2" data-testid="enroll-table">
      {/* Column header */}
      <div className="grid grid-cols-[24px_2fr_1.2fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold border-b">
        <div></div>
        <div>Payer</div>
        <div>Overall</div>
        <div className="text-center">Group Contract</div>
        <div className="text-center">Providers</div>
        <div className="text-center">ERA</div>
        <div className="text-center">EFT</div>
        <div>Priority</div>
        <div>Assigned</div>
      </div>

      {filtered.map(r => {
        const open = !!expanded[r.enrollment_id];
        const overallTone = OVERALL_STATUS_TONE[r.status] || OVERALL_STATUS_TONE.pending;
        return (
          <div key={r.enrollment_id} className="rounded-xl border border-[#E8E5EC] bg-white hover:shadow-sm transition-shadow" data-testid={`enroll-row-${r.enrollment_id}`}>
            {/* Compact row */}
            <div className="grid grid-cols-[24px_2fr_1.2fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-3 py-2 items-center">
              <button
                onClick={() => toggle(r.enrollment_id)}
                className="text-slate-400 hover:text-slate-700 text-sm w-6 h-6 rounded hover:bg-slate-100 flex items-center justify-center"
                data-testid={`enroll-toggle-${r.enrollment_id}`}
                aria-label="Toggle details"
              >{open ? '▾' : '▸'}</button>
              <div className="font-semibold text-sm text-slate-800">{r.payer_name}</div>
              <select
                value={r.status}
                onChange={e => setField(r.enrollment_id, 'status', e.target.value)}
                className={`text-xs px-2 py-1 rounded border ${overallTone}`}
                data-testid={`enroll-status-${r.enrollment_id}`}
              >
                {ENROLLMENT_STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
              <SubStatusSelect value={r.group_contract} onChange={v => setField(r.enrollment_id, 'group_contract', v)} tid={`enroll-gc-${r.enrollment_id}`} />
              <SubStatusSelect value={r.providers_enrolled} onChange={v => setField(r.enrollment_id, 'providers_enrolled', v)} tid={`enroll-pe-${r.enrollment_id}`} />
              <SubStatusSelect value={r.era_setup} onChange={v => setField(r.enrollment_id, 'era_setup', v)} tid={`enroll-era-${r.enrollment_id}`} />
              <SubStatusSelect value={r.eft_setup} onChange={v => setField(r.enrollment_id, 'eft_setup', v)} tid={`enroll-eft-${r.enrollment_id}`} />
              <select
                value={r.priority}
                onChange={e => setField(r.enrollment_id, 'priority', e.target.value)}
                className="text-xs px-2 py-1 rounded border border-slate-200 bg-white"
                data-testid={`enroll-priority-${r.enrollment_id}`}
              >
                {PRIORITY.map(p => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}
              </select>
              <div className="text-xs">
                <EditText value={r.assigned_admin} onCommit={v => setField(r.enrollment_id, 'assigned_admin', v)} tid={`enroll-admin-${r.enrollment_id}`} placeholder="Assign…" />
              </div>
            </div>

            {/* Expandable detail */}
            {open && (
              <div className="px-3 pb-4 pt-1 border-t border-slate-100 bg-slate-50/40 rounded-b-xl" data-testid={`enroll-detail-${r.enrollment_id}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <DetailField label="Application Submitted"><EditDate value={r.app_submitted_date} onCommit={v => setField(r.enrollment_id, 'app_submitted_date', v)} tid={`enroll-submitted-${r.enrollment_id}`} /></DetailField>
                  <DetailField label="Follow-up Due"><EditDate value={r.follow_up_due_date} onCommit={v => setField(r.enrollment_id, 'follow_up_due_date', v)} tid={`enroll-follow-${r.enrollment_id}`} /></DetailField>
                  <DetailField label="Approved / Effective"><EditDate value={r.approved_date} onCommit={v => setField(r.enrollment_id, 'approved_date', v)} tid={`enroll-approved-${r.enrollment_id}`} /></DetailField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <DetailField label="Next Action">
                    <EditText value={r.next_action} onCommit={v => setField(r.enrollment_id, 'next_action', v)} tid={`enroll-action-${r.enrollment_id}`} placeholder="e.g. Email Kim to confirm receipt" />
                  </DetailField>
                  <DetailField label="Contact (phone / email / name)">
                    <EditText value={r.contact} onCommit={v => setField(r.enrollment_id, 'contact', v)} tid={`enroll-contact-${r.enrollment_id}`} placeholder="e.g. 833-731-2162 or credentialing@…" />
                  </DetailField>
                </div>
                <div className="mt-3">
                  <DetailField label="Notes">
                    <NotesEditor value={r.notes} onCommit={v => setField(r.enrollment_id, 'notes', v)} tid={`enroll-notes-${r.enrollment_id}`} />
                  </DetailField>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="py-6 text-center text-slate-400">No payer enrollments match the search</div>
      )}
    </div>
  );
}

function SubStatusSelect({ value, onChange, tid }) {
  return (
    <select
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      className={`text-xs px-2 py-1 rounded border w-full ${SUB_STATUS_TONE[value || ''] || SUB_STATUS_TONE['']}`}
      data-testid={tid}
    >
      {SUB_STATUS.map(s => <option key={s.v} value={s.v}>{s.l}</option>)}
    </select>
  );
}

function DetailField({ label, children }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function NotesEditor({ value, onCommit, tid }) {
  const [v, setV] = useState(value ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value ?? ''); }, [value]);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="w-full text-left rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-700 hover:border-slate-400 min-h-[60px]"
        data-testid={tid}
      >
        {value ? <span className="whitespace-pre-wrap">{value}</span> : <span className="text-slate-400 italic">Add notes — history, gotchas, contact preferences, anything worth remembering…</span>}
      </button>
    );
  }
  return (
    <Textarea
      autoFocus
      rows={3}
      value={v}
      onChange={e => setV(e.target.value)}
      onBlur={() => { setEditing(false); if (v !== (value ?? '')) onCommit(v); }}
      onKeyDown={e => { if (e.key === 'Escape') { setV(value ?? ''); setEditing(false); } }}
      className="text-xs"
      data-testid={`${tid}-input`}
    />
  );
}

const KANBAN_COLUMNS = [
  { v: 'pending', l: 'Pending', tone: 'bg-slate-100 text-slate-700 border-slate-200' },
  { v: 'in_progress', l: 'In Progress', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  { v: 'done', l: 'Complete', tone: 'bg-green-50 text-green-800 border-green-200' },
  { v: 'blocked', l: 'Blocked', tone: 'bg-red-50 text-red-800 border-red-200' },
  { v: 'rejected', l: 'Rejected', tone: 'bg-red-100 text-red-900 border-red-300' },
];

function SubStatusPill({ label, value, tid }) {
  const v = (value || '').toString().toLowerCase();
  const tone = v === 'complete' ? 'bg-green-100 text-green-800 border-green-200'
    : v === 'in_progress' ? 'bg-amber-100 text-amber-800 border-amber-200'
    : v === 'blocked' ? 'bg-red-100 text-red-800 border-red-200'
    : v === 'not_applicable' ? 'bg-slate-100 text-slate-500 border-slate-200'
    : v === 'pending' ? 'bg-slate-50 text-slate-600 border-slate-200'
    : 'bg-white text-slate-400 border-dashed border-slate-300';
  const glyph = v === 'complete' ? '✓' : v === 'blocked' ? '✕' : v === 'in_progress' ? '~' : v === 'not_applicable' ? '–' : v === 'pending' ? '·' : '·';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${tone}`}
      title={`${label}: ${v || 'not set'}`}
      data-testid={tid}
    >
      <span className="font-mono">{glyph}</span>
      <span className="font-semibold">{label}</span>
    </span>
  );
}

function EnrollmentKanban({ filtered, setField }) {
  const [dragId, setDragId] = useState(null);

  const onDrop = async (status) => {
    if (!dragId) return;
    setField(dragId, 'status', status);
    setDragId(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="enroll-kanban">
      {KANBAN_COLUMNS.map(col => {
        const cards = filtered.filter(r => r.status === col.v);
        return (
          <div
            key={col.v}
            onDragOver={e => e.preventDefault()}
            onDrop={() => onDrop(col.v)}
            className={`rounded-xl border ${col.tone} p-3 min-h-[300px]`}
            data-testid={`enroll-kanban-col-${col.v}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase font-bold tracking-widest">{col.l}</div>
              <div className="text-xs px-2 py-0.5 rounded-full bg-white/70 border border-current/20">{cards.length}</div>
            </div>
            <div className="space-y-2">
              {cards.map(r => (
                <div
                  key={r.enrollment_id}
                  draggable
                  onDragStart={() => setDragId(r.enrollment_id)}
                  onDragEnd={() => setDragId(null)}
                  className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm hover:shadow cursor-grab active:cursor-grabbing"
                  data-testid={`enroll-kanban-card-${r.enrollment_id}`}
                >
                  <div className="font-semibold text-sm text-slate-800">{r.payer_name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">
                    {r.priority || 'medium'} priority
                  </div>
                  {/* 4 sub-status pills */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    <SubStatusPill label="GC" value={r.group_contract} tid={`enroll-kbpill-gc-${r.enrollment_id}`} />
                    <SubStatusPill label="Prov" value={r.providers_enrolled} tid={`enroll-kbpill-pe-${r.enrollment_id}`} />
                    <SubStatusPill label="ERA" value={r.era_setup} tid={`enroll-kbpill-era-${r.enrollment_id}`} />
                    <SubStatusPill label="EFT" value={r.eft_setup} tid={`enroll-kbpill-eft-${r.enrollment_id}`} />
                  </div>
                  {r.assigned_admin && (
                    <div className="text-xs mt-2 text-slate-600">
                      <span className="text-slate-400">Owner:</span> {r.assigned_admin}
                    </div>
                  )}
                  {r.next_action && (
                    <div className="text-xs mt-1 text-slate-600 line-clamp-2">
                      <span className="text-slate-400">Next:</span> {r.next_action}
                    </div>
                  )}
                  {r.follow_up_due_date && (
                    <div className="text-[10px] mt-1.5 text-amber-800 bg-amber-50 rounded px-1.5 py-0.5 inline-block">
                      Follow-up {r.follow_up_due_date}
                    </div>
                  )}
                </div>
              ))}
              {cards.length === 0 && (
                <div className="text-xs text-slate-400 text-center py-6 italic">Drop here…</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EditDate({ value, onCommit, tid, display }) {
  const [v, setV] = useState(value ?? '');
  const [editing, setEditing] = useState(false);
  useEffect(() => { setV(value ?? ''); }, [value]);
  const shown = (display || ((x) => {
    if (!x) return '—';
    const m = String(x).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}-${m[3]}-${m[1]}` : String(x);
  }))(value);
  if (!editing) return (
    <button
      onClick={() => setEditing(true)}
      className="text-xs hover:underline text-slate-700"
      data-testid={tid}
    >
      {shown}
    </button>
  );
  return (
    <input
      type="date"
      value={v || ''}
      autoFocus
      onChange={e => setV(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (v !== (value ?? '')) onCommit(v || '');
      }}
      className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
      data-testid={`${tid}-input`}
    />
  );
}

// ═════════════════ Training Matrix ═════════════════

function TrainingMatrix({ query }) {
  const [rows, setRows] = useState([]);
  const [staffNames, setStaffNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStaff, setNewStaff] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/vault/training-matrix');
      setRows(data.rows || []);
      setStaffNames(data.staff || []);
    } catch { toast.error('Could not load training matrix'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addStaff = async () => {
    const name = newStaff.trim();
    if (!name) return;
    try {
      await api.post('/vault/training-matrix/staff', { name });
      toast.success(`Added ${name}`);
      setNewStaff('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const removeStaff = async (name) => {
    if (!window.confirm(`Remove ${name} from every training row?`)) return;
    try {
      await api.delete(`/vault/training-matrix/staff/${encodeURIComponent(name)}`);
      toast.success(`Removed ${name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filtered = useMemo(
    () => rows.filter(r => matches(query, r.category, r.skill_area, r.gap_action)),
    [rows, query],
  );

  const setScore = async (row_id, name, score) => {
    const row = rows.find(r => r.row_id === row_id);
    const scores = { ...(row.scores || {}), [name]: score };
    try {
      const { data: updated } = await api.patch(`/vault/training-matrix/${row_id}`, { scores });
      setRows(rs => rs.map(r => r.row_id === row_id ? updated : r));
    } catch { toast.error('Save failed'); }
  };

  const setField = async (row_id, field, value) => {
    try {
      const { data: updated } = await api.patch(`/vault/training-matrix/${row_id}`, { [field]: value });
      setRows(rs => rs.map(r => r.row_id === row_id ? updated : r));
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <Card className="p-6">Loading…</Card>;

  // Group by category
  const byCategory = {};
  filtered.forEach(r => { (byCategory[r.category || 'GENERAL'] ||= []).push(r); });

  return (
    <Card className="p-4 overflow-auto" data-testid="vault-training">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="text-xs text-slate-500">
          Score 0–5. A <span className="inline-block px-1 rounded bg-red-100 text-red-700">0</span> means "not trained" and is flagged red — that's your backlog.
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            value={newStaff}
            onChange={e => setNewStaff(e.target.value)}
            placeholder="Add a staff member…"
            className="w-56"
            data-testid="train-staff-input"
          />
          <Button size="sm" onClick={addStaff} data-testid="train-staff-add">
            <Plus size={12} className="mr-1" /> Add Staff Column
          </Button>
        </div>
      </div>
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
          <tr>
            <th className="py-2 pr-3">Skill</th>
            {staffNames.map(n => (
              <th key={n} className="py-2 pr-3 text-center">
                <div className="inline-flex items-center gap-1">
                  <span>{n}</span>
                  <button
                    onClick={() => removeStaff(n)}
                    className="text-slate-400 hover:text-red-500"
                    title={`Remove ${n} from every row`}
                    data-testid={`train-staff-remove-${n}`}
                  >
                    <X size={11} />
                  </button>
                </div>
              </th>
            ))}
            <th className="py-2 pr-3">Gap / Action</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(byCategory).map(([cat, catRows]) => (
            <React.Fragment key={cat}>
              <tr className="bg-slate-50">
                <td colSpan={2 + staffNames.length} className="py-2 px-3 text-xs uppercase font-semibold tracking-widest text-slate-500">{cat}</td>
              </tr>
              {catRows.map(r => (
                <tr key={r.row_id} className="border-b" data-testid={`train-row-${r.row_id}`}>
                  <td className="py-2 pr-3 font-medium">
                    <EditText value={r.skill_area} onCommit={v => setField(r.row_id, 'skill_area', v)} tid={`train-skill-${r.row_id}`} />
                  </td>
                  {staffNames.map(n => {
                    const score = r.scores?.[n];
                    const isZero = score === 0;
                    return (
                      <td key={n} className="py-2 pr-3 text-center">
                        <select
                          value={score ?? ''}
                          onChange={e => setScore(r.row_id, n, e.target.value === '' ? null : Number(e.target.value))}
                          className={`text-xs px-2 py-1 rounded border ${isZero ? 'bg-red-100 text-red-800 border-red-200 font-bold' : 'bg-white border-slate-200'}`}
                          data-testid={`train-score-${r.row_id}-${n}`}
                        >
                          <option value="">—</option>
                          {[0,1,2,3,4,5].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    );
                  })}
                  <td className="py-2 pr-3 text-xs text-slate-600">
                    <EditText value={r.gap_action} onCommit={v => setField(r.row_id, 'gap_action', v)} tid={`train-gap-${r.row_id}`} placeholder="—" />
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// HIPAA §164.312(b) — Audit Controls. Renders the hash-chained
// security_audit stream + vault_audit_log merged into a single
// timeline, with filters for actor / action / PHI-only and a
// one-click integrity verifier that walks the hash chain.
function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [phiOnly, setPhiOnly] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/audit-log', {
        params: {
          limit: 500,
          actor: actorFilter || undefined,
          action: actionFilter || undefined,
          phi_only: phiOnly || undefined,
        },
      });
      setRows(data.rows || []);
    } catch { toast.error('Could not load audit log'); }
    finally { setLoading(false); }
  }, [actorFilter, actionFilter, phiOnly]);
  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.get('/admin/audit-log/verify');
      setVerifyResult(data);
      if (data.ok) toast.success(`Chain verified · ${data.verified_rows} rows, no tampering`);
      else toast.error(`Chain integrity failed: ${data.reason}`);
    } catch { toast.error('Verify failed'); }
    finally { setVerifying(false); }
  };

  const fmtWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d)) return String(iso).slice(0, 19);
    return d.toLocaleString();
  };

  return (
    <Card className="p-4" data-testid="vault-audit-log">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-sm text-[#685B72] mr-2">
          <span className="font-serif text-[#2D2833]">{rows.length}</span> event{rows.length === 1 ? '' : 's'}
        </div>
        <Input
          value={actorFilter}
          onChange={e => setActorFilter(e.target.value)}
          placeholder="Filter by actor email…"
          className="h-8 max-w-[220px] text-[12px]"
          data-testid="audit-actor-filter"
        />
        <Input
          value={actionFilter}
          onChange={e => setActionFilter(e.target.value)}
          placeholder="Filter by action prefix…"
          className="h-8 max-w-[200px] text-[12px]"
          data-testid="audit-action-filter"
        />
        <label className="text-[12px] text-[#685B72] flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={phiOnly}
            onChange={e => setPhiOnly(e.target.checked)}
            data-testid="audit-phi-only"
          />
          PHI accesses only
        </label>
        <div className="flex-1" />
        <Button
          size="sm" variant="outline"
          onClick={load}
          className="h-8"
          data-testid="audit-refresh"
        >
          <RefreshCw size={12} className="mr-1" /> Refresh
        </Button>
        <Button
          size="sm"
          onClick={verify}
          disabled={verifying}
          className="h-8 bg-[#685B72] hover:bg-[#4A3B5F] text-white text-[12px] font-semibold"
          data-testid="audit-verify"
        >
          <ShieldCheck size={12} className="mr-1" />
          {verifying ? 'Verifying…' : 'Verify integrity'}
        </Button>
      </div>

      {verifyResult && (
        <div
          className={`mb-3 rounded-lg px-3 py-2 text-[12px] ${
            verifyResult.ok ? 'bg-[#E9F1EE] text-[#3B6F5D] border border-[#B8D3C7]'
                            : 'bg-[#F5EAEA] text-[#8A3A3A] border border-[#D9B4B4]'
          }`}
          data-testid="audit-verify-result"
        >
          {verifyResult.ok ? (
            <>✓ Chain intact — {verifyResult.verified_rows} rows verified, last seq {verifyResult.last_seq}</>
          ) : (
            <>⚠ Chain broken at row #{verifyResult.at_index} · reason: <span className="font-semibold">{verifyResult.reason}</span>{verifyResult.row_at && <> · row_at {verifyResult.row_at}</>}</>
          )}
        </div>
      )}

      {loading ? (
        <div className="p-6 text-[#685B72]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 text-center text-[#685B72]" data-testid="audit-empty">
          No audit rows match those filters.
        </div>
      ) : (
        <div className="overflow-auto max-h-[70vh] border border-[#EFEBF3] rounded-lg" data-testid="audit-table">
          <table className="min-w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-widest text-[#685B72] bg-[#FAF8FC] sticky top-0">
              <tr>
                <th className="py-2 px-3">When</th>
                <th className="py-2 px-3">Actor</th>
                <th className="py-2 px-3">Action</th>
                <th className="py-2 px-3">Target</th>
                <th className="py-2 px-3">PHI</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">IP</th>
                <th className="py-2 px-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b border-[#F1EEF3] hover:bg-[#FAF8FC]"
                    data-testid={`audit-row-${i}`}>
                  <td className="py-2 px-3 tabular-nums text-[#2D2833] whitespace-nowrap">{fmtWhen(r.at)}</td>
                  <td className="py-2 px-3 text-[#2D2833]" title={r.meta?.role || ''}>{r.actor || '—'}</td>
                  <td className="py-2 px-3 font-mono text-[#4A3B5F]">{r.action}</td>
                  <td className="py-2 px-3 text-[#685B72] max-w-[220px] truncate" title={r.target}>{r.target || '—'}</td>
                  <td className="py-2 px-3">
                    {r.phi ? (
                      <span className="inline-flex text-[9px] px-1.5 py-0.5 rounded-full bg-[#F9F2E3] text-[#8A5D2D] border border-[#B08A3A66]">PHI</span>
                    ) : ''}
                  </td>
                  <td className="py-2 px-3">
                    <span className={`inline-flex text-[9px] px-1.5 py-0.5 rounded-full border ${
                      r.status === 'ok' ? 'bg-green-50 text-green-700 border-green-200'
                                        : r.status === 'denied' ? 'bg-red-50 text-red-700 border-red-200'
                                        : 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                      {r.status || 'ok'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-[#685B72] font-mono text-[10px]">{r.ip || '—'}</td>
                  <td className="py-2 px-3 text-[10px] text-[#685B72]">{r._source === 'vault_audit_log' ? 'vault' : 'security'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-3 text-[10px] text-[#685B72]">
        HIPAA §164.312(b) · Audit rows are hash-chained and append-only — no editor exposed, no delete endpoint.
        The <span className="font-semibold">Verify integrity</span> action walks the chain and flags any tampering.
      </div>
    </Card>
  );
}


